import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const PaystubViewer = ({ paystubData }) => {
  const [storeData, setStoreData] = useState([]);
  const [periodInfo, setPeriodInfo] = useState(null);
  const [deductionsLoaded, setDeductionsLoaded] = useState(false); // Track deduction loading state
  const formatDate = (dateStr) => {
    // Split the date string to avoid timezone issues
    const [year, month, day] = dateStr.split('-');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const calculateNetPay = () => {
    const grossPay = parseFloat(paystubData.total_pay || 0);
    const deductions = parseFloat(paystubData.employee_deductions || 0);
    const taxes = parseFloat(paystubData.employee_taxes || 0);
    return grossPay - deductions - taxes;
  };

  const getDeductions = () => {
    // If deductions are already in paystubData (from App.js merge), use those
    // Otherwise, try to extract from the paystub data fields
    return {
      employee_deductions: parseFloat(paystubData.employee_deductions || 0),
      employee_taxes: parseFloat(paystubData.employee_taxes || 0)
    };
  };

  // Enhanced function to fetch deductions if not already loaded
  const fetchDeductionsIfNeeded = async () => {
    // Check if deductions are already loaded and greater than 0
    const currentDeductions = parseFloat(paystubData.employee_deductions || 0);
    const currentTaxes = parseFloat(paystubData.employee_taxes || 0);
    
    if (currentDeductions > 0 || currentTaxes > 0) {
      return; // Already have deductions
    }

    // If no deductions loaded, try to fetch from payroll_dynamics
    try {
      const companyCode = paystubData.company;
      
      // First try to get the employee data by employee_number, then fallback to employee_name
      let employeeData = null;
      let employeeError = null;
      
      // Try by employee_number first
      const { data: empByNumber, error: errByNumber } = await supabase
        .from('employees')
        .select('employee_id_kf, employee_id_koo, employee_number, payroll_fname, payroll_lname')
        .eq('employee_number', paystubData.employee_number);
      
      if (empByNumber && empByNumber.length > 0) {
        employeeData = empByNumber[0];
      } else {
        // Fallback to searching by combined name in payroll_summary, then get employee data
        const { data: empByName, error: errByName } = await supabase
          .from('payroll_summary')
          .select('employee_number')
          .eq('employee_name', paystubData.employee_name)
          .limit(1);
          
        if (empByName && empByName.length > 0) {
          // Now get the full employee data from employees table
          const { data: fullEmpData, error: fullEmpError } = await supabase
            .from('employees')
            .select('employee_id_kf, employee_id_koo, employee_number, payroll_fname, payroll_lname')
            .eq('employee_number', empByName[0].employee_number)
            .single();
            
          if (fullEmpData && !fullEmpError) {
            employeeData = fullEmpData;
          } else {
            employeeError = fullEmpError || { message: 'Employee data not found in employees table' };
          }
        } else {
          employeeError = errByName || { message: 'Employee not found by number or name' };
        }
      }

      if (employeeError || !employeeData) {
        return;
      }

      // Match employee_id based on company
      let employeeIdToMatch = null;
      if (companyCode === 'KF' && employeeData.employee_id_kf) {
        employeeIdToMatch = employeeData.employee_id_kf;
      } else if (companyCode === 'KOO' && employeeData.employee_id_koo) {
        employeeIdToMatch = employeeData.employee_id_koo;
      }

      if (!employeeIdToMatch) {
        return;
      }

      // Fetch deductions from payroll_dynamics for the specific payroll period
      const { data: deductionsData, error: deductionsError } = await supabase
        .from('payroll_dynamics')
        .select('employee_deductions, employee_taxes, net_payment, employee_id, company, payroll_number')
        .eq('employee_id', employeeIdToMatch)
        .eq('company', companyCode)
        .eq('payroll_number', paystubData.payroll_number);

      if (deductionsError) {
        return;
      }

      if (deductionsData && deductionsData.length > 0) {
        // Use the first matching record
        const deductionData = deductionsData[0];
        
        // Update paystubData with deductions (modify the object directly)
        paystubData.employee_deductions = parseFloat(deductionData.employee_deductions || 0);
        paystubData.employee_taxes = parseFloat(deductionData.employee_taxes || 0);
        paystubData.net_payment = parseFloat(deductionData.net_payment || 0);
        
        // Trigger a re-render by updating state
        setDeductionsLoaded(true);
      }
    } catch (error) {
      // Silent error handling
    }
  };

  useEffect(() => {
    if (paystubData?.employee_name && paystubData?.payroll_number) {
      fetchPeriodInfo();
      fetchDeductionsIfNeeded(); // Fetch deductions if not already loaded
    }
  }, [paystubData?.employee_name, paystubData?.payroll_number]); // eslint-disable-line react-hooks/exhaustive-deps

  // Separate useEffect for store data that depends on periodInfo
  useEffect(() => {
    if (periodInfo && paystubData?.employee_name) {
      fetchStoreData();
    }
  }, [periodInfo, paystubData?.employee_name]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPeriodInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('payroll_period')
        .select('period_label, start_date, end_date, pay_date')
        .eq('payroll_number', paystubData.payroll_number)
        .single();

      if (error) {
        return;
      }

      setPeriodInfo(data);
    } catch (error) {
      // Silent error handling
    }
  };

  const fetchStoreData = async () => {
    if (!periodInfo) {
      return;
    }
    
    try {
      // For payroll statements: fetch attendance only for the specific company
      let query = supabase
        .from('payroll_attendance')
        .select('employee_name, date, day_name, store_name, hours, company')
        .eq('employee_name', paystubData.employee_name)
        .gt('hours', 0)
        .gte('date', periodInfo.start_date)
        .lte('date', periodInfo.end_date);
      
      // Filter by company for payroll statements
      if (paystubData.company) {
        query = query.eq('company', paystubData.company);
      }
      
      const { data, error } = await query.order('date');

      if (error) {
        return;
      }
      
      // Remove duplicates by creating a map with date as key, keeping the first occurrence
      const uniqueData = [];
      const seenDates = new Set();
      
      if (data) {
        data.forEach(record => {
          if (!seenDates.has(record.date)) {
            uniqueData.push(record);
            seenDates.add(record.date);
          }
        });
      }
      
      setStoreData(uniqueData);
    } catch (error) {
      // Silent error handling
    }
  };

  const getStoreCode = (storeName) => {
    if (!storeName) return '';
    
    // Normalize store name for better matching
    const normalizedName = storeName.trim();
    
    const storeCodes = {
      'Main St': 'M',
      'Main Street': 'M',
      'Kissena Blvd': 'K',
      'Kissena': 'K',
      '39th Ave': '39TH',
      '39TH': '39TH',
      'Roosevelt Ave': 'R',
      'Roosevelt': 'R',
      'Albany': 'AL',
      'Arbutus': 'ARB',
      'Columbia': 'CM',
      'Ellicott City': 'EC',
      'Essex': 'EX',
      'Frederick': 'FK',
      'Glen Burnie': 'GL',
      'Highland': 'HL',
      'Laurel': 'LR',
      'Odenton': 'OD',
      'Owings Mills': 'OW',
      'Pikesville': 'PV',
      'Rockville Plaza': 'RP',
      'Rio': 'RR',
      'Silver Spring': 'SC',
      'Severna Park': 'SV',
      'Towson': 'TW',
      'White Marsh': 'WH',
      'Falls Church': 'FLS',
      'Germantown Commons': 'GTC'
    };
    
    // First try exact match
    if (storeCodes[normalizedName]) {
      return storeCodes[normalizedName];
    }
    
    // Try partial matches for common variations
    const lowerName = normalizedName.toLowerCase();
    for (const [storeName, code] of Object.entries(storeCodes)) {
      if (lowerName.includes(storeName.toLowerCase()) || storeName.toLowerCase().includes(lowerName)) {
        return code;
      }
    }
    
    // If no match found, return the original name truncated
    return normalizedName.length > 4 ? normalizedName.substring(0, 4).toUpperCase() : normalizedName.toUpperCase();
  };

  const generateCalendarData = () => {
    if (!periodInfo) return [];
    
    const calendarData = [];
    const weekDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    
    // Generate dynamic date mapping based on actual period dates
    const startDate = new Date(periodInfo.start_date + 'T00:00:00');
    const endDate = new Date(periodInfo.end_date + 'T00:00:00');
    
    // Calculate all dates in the period (should be exactly 14 days)
    const periodDates = [];
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      periodDates.push(new Date(d).toISOString().split('T')[0]);
    }
    
    // Map dates to calendar positions (Week 1: first 7 days, Week 2: next 7 days)
    const dateToCalendarPosition = {};
    periodDates.forEach((date, index) => {
      const week = Math.floor(index / 7) + 1;
      const dayIndex = index % 7;
      const dayName = weekDays[dayIndex];
      const position = `week${week}_${dayName}`;
      dateToCalendarPosition[date] = position;
    });
    
    // Now generate the calendar structure using the paystub data fields
    for (let week = 1; week <= 2; week++) {
      const weekData = [];
      for (const day of weekDays) {
        const fieldName = `week${week}_${day}`;
        let hours = paystubData[fieldName] || 0;
        
        // Find the actual date for this calendar position
        const actualDate = Object.keys(dateToCalendarPosition).find(
          date => dateToCalendarPosition[date] === fieldName
        );
        
        // Find store info from payroll_attendance data using the actual date
        // Filter by company to show only company-specific hours in payroll statements
        const storeInfo = actualDate ? storeData.find(item => 
          item.date === actualDate && item.company === paystubData.company
        ) : null;
        const storeCode = getStoreCode(storeInfo?.store_name);
        
        // If paystub field is 0 but we have store data with hours, use store data
        if (hours === 0 && storeInfo && parseFloat(storeInfo.hours) > 0) {
          hours = parseFloat(storeInfo.hours);
        }
        
        weekData.push({
          day: day.charAt(0).toUpperCase() + day.slice(1),
          hours: parseFloat(hours),
          store: storeCode,
          storeName: storeInfo?.store_name || '',
          date: actualDate || '',
          rawStoreData: storeInfo // Include raw data for debugging
        });
      }
      calendarData.push(weekData);
    }
    
    return calendarData;
  };

  const calendarData = generateCalendarData();
  
  // For payroll statements: calculate timesheet hours by subtracting benefit hours from total_hours
  const totalHoursFromPayroll = parseFloat(paystubData.total_hours || 0);
  const ptoHours = parseFloat(paystubData.pto_hp || 0);
  const sickLeaveHours = parseFloat(paystubData.sick_leave || 0);
  const lunchHours = parseFloat(paystubData.lunch || 0);
  const benefitHours = ptoHours + sickLeaveHours + lunchHours;
  
  // Timesheet hours = total hours - benefit hours (for individual company payroll statements)
  const timesheetHours = totalHoursFromPayroll - benefitHours;
  
  const netPay = calculateNetPay();
  const deductions = getDeductions();
  
  // Total hours = timesheet + benefit hours (should match totalHours from payroll_summary)
  const totalAdditionalHours = benefitHours;
  const totalHours = timesheetHours + totalAdditionalHours;

  return (
    <div className="bg-white rounded-none sm:rounded-lg shadow-lg overflow-hidden w-full max-w-4xl mx-auto min-h-screen sm:min-h-0 paystub-viewer-mobile">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold mb-1 sm:mb-2">{paystubData.employee_name || 'STAFF NAME'}</h1>
            <p className="text-blue-100 text-xs sm:text-base">Pay Period: {paystubData.pay_period || 'N/A'}</p>
            <p className="text-red-400 font-bold text-xs sm:text-sm mt-1 sm:mt-2 leading-tight">
              This is for personal information only, DO NOT SHARE!<br />
              USE Payroll Dynamics Pay Stub as official payroll statement.
            </p>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-xs sm:text-sm text-blue-100">Pay Date</p>
            <p className="text-base sm:text-xl font-semibold">
              {formatDate(periodInfo?.pay_date || paystubData.pay_date || '2025-06-27')}
            </p>
          </div>
        </div>
      </div>

      {/* Employee Information */}
      <div className="p-3 sm:p-6 bg-gray-50">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <div>
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Employee</h3>
            <p className="mt-1 text-sm sm:text-lg font-semibold text-gray-900">{paystubData.employee_name}</p>
            <p className="text-xs sm:text-sm text-gray-600">#{paystubData.employee_number}</p>
          </div>
          <div>
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Company</h3>
            <p className="mt-1 text-sm sm:text-lg font-semibold text-gray-900">
              {paystubData.company === 'Combined' && paystubData.originalPaystubs 
                ? paystubData.originalPaystubs.map(p => p.company).join(', ')
                : paystubData.company}
            </p>
          </div>
          <div className="sm:col-span-2 lg:col-span-1">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Department</h3>
            <p className="mt-1 text-sm sm:text-lg font-semibold text-gray-900">{paystubData.department}</p>
          </div>
        </div>
      </div>

      {/* Pay Summary */}
      <div className="p-3 sm:p-6">
        <h2 className="text-base sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Pay Summary</h2>
        <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <div className="flex justify-between py-1 sm:py-2">
                <span className="text-xs sm:text-sm text-gray-600">Gross Pay:</span>
                <span className="text-xs sm:text-sm font-semibold">{formatCurrency(paystubData.total_pay)}</span>
              </div>
              <div className="flex justify-between py-1 sm:py-2 pt-2 sm:pt-3 border-t-2 border-gray-300">
                <span className="text-sm sm:text-lg font-bold">Net Pay:</span>
                <span className="text-sm sm:text-lg font-bold text-green-600">{formatCurrency(netPay)}</span>
              </div>
              <div className="flex justify-between py-1 sm:py-2 text-xs sm:text-sm text-blue-600">
                <span>Direct Deposit:</span>
                <span className="font-semibold">{formatCurrency(paystubData.net_payment || 0)}</span>
              </div>
              <div className="flex justify-between py-1 sm:py-2 text-xs sm:text-sm text-orange-600">
                <span>Manual Pay:</span>
                <span className="font-semibold">{formatCurrency((parseFloat(paystubData.weekday_cash_pay || 0) + parseFloat(paystubData.weekend_cash_pay || 0)))}</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between py-1 sm:py-2">
                <span className="text-xs sm:text-sm text-gray-600">Timesheet Hours:</span>
                <span className="text-xs sm:text-sm font-semibold">{timesheetHours}</span>
              </div>
              {totalAdditionalHours > 0 && (
                <>
                  <div className="flex justify-between py-1 sm:py-2 text-xs sm:text-sm text-gray-500">
                    <span>Benefit Hours:</span>
                    <span>{totalAdditionalHours}</span>
                  </div>
                  {ptoHours > 0 && (
                    <div className="flex justify-between py-0.5 sm:py-1 pl-2 sm:pl-4 text-xs sm:text-sm">
                      <span className="text-gray-600">• PTO/HP:</span>
                      <span className="font-medium">{ptoHours}</span>
                    </div>
                  )}
                  {sickLeaveHours > 0 && (
                    <div className="flex justify-between py-0.5 sm:py-1 pl-2 sm:pl-4 text-xs sm:text-sm">
                      <span className="text-gray-600">• Sick Leave:</span>
                      <span className="font-medium">{sickLeaveHours}</span>
                    </div>
                  )}
                  {lunchHours > 0 && (
                    <div className="flex justify-between py-0.5 sm:py-1 pl-2 sm:pl-4 text-xs sm:text-sm">
                      <span className="text-gray-600">• Lunch:</span>
                      <span className="font-medium">{lunchHours}</span>
                    </div>
                  )}
                  {/* Show multi-company indicator if benefit hours are aggregated */}
                  {paystubData.employee_id_kf && paystubData.employee_id_koo && totalAdditionalHours > 0 && (
                    <div className="text-xs text-gray-400 mt-1 italic pl-2 sm:pl-4">
                      * Combined from KF & KOO companies
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between py-1 sm:py-2 pt-2 sm:pt-3 border-t border-gray-300">
                <span className="text-xs sm:text-sm text-gray-600 font-medium">Total Hours:</span>
                <span className="text-xs sm:text-sm font-semibold">{totalHours}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compensation Breakdown */}
      <div className="p-3 sm:p-6 bg-gray-50">
        <h2 className="text-base sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Compensation Breakdown</h2>
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-1 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-1 sm:px-4 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                <th className="px-1 sm:px-4 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                <th className="px-1 sm:px-4 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {parseFloat(paystubData.check_hours || 0) > 0 && (
                <tr>
                  <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-medium text-gray-900">Direct Deposit</td>
                  <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right">{paystubData.check_hours}</td>
                  <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right">{formatCurrency(paystubData.check_rate)}</td>
                  <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-medium text-gray-900 text-right">{formatCurrency(paystubData.check_pay)}</td>
                </tr>
              )}
              {parseFloat(paystubData.weekday_cash_hours || 0) > 0 && (
                <tr>
                  <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-medium text-gray-900">Manual Pay (Weekday)</td>
                  <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right">{paystubData.weekday_cash_hours}</td>
                  <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right">{formatCurrency(paystubData.weekday_cash_rate)}</td>
                  <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-medium text-gray-900 text-right">{formatCurrency(paystubData.weekday_cash_pay)}</td>
                </tr>
              )}
              {parseFloat(paystubData.weekend_cash_hours || 0) > 0 && (
                <tr>
                  <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-medium text-gray-900">Manual Pay (Weekend)</td>
                  <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right">{paystubData.weekend_cash_hours}</td>
                  <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right">{formatCurrency(paystubData.weekend_cash_rate)}</td>
                  <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-medium text-gray-900 text-right">{formatCurrency(paystubData.weekend_cash_pay)}</td>
                </tr>
              )}
              <tr>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-bold text-gray-900">Total Gross Pay</td>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right">{totalHours}</td>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right"></td>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-bold text-gray-900 text-right">{formatCurrency(paystubData.total_pay)}</td>
              </tr>
              <tr>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-medium text-gray-900">Employee Deductions</td>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right"></td>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right"></td>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-medium text-red-600 text-right">-{formatCurrency(deductions.employee_deductions)}</td>
              </tr>
              <tr>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-medium text-gray-900">Employee Taxes</td>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right"></td>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right"></td>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-medium text-red-600 text-right">-{formatCurrency(deductions.employee_taxes)}</td>
              </tr>
              <tr>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-bold text-gray-900">Net Pay</td>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right"></td>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs text-gray-500 text-right"></td>
                <td className="px-1 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs font-bold text-green-600 text-right">{formatCurrency(netPay)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Biweekly Timesheet Calendar */}
      <div className="p-3 sm:p-6 bg-gray-50">
        <h2 className="text-base sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Biweekly Timesheet Calendar</h2>
        <p className="text-xs text-gray-600 mb-3 sm:mb-4">
          Pay Period: {periodInfo?.period_label || 'Loading...'} | Store Codes: M=Main St, K=Kissena, 39TH=39th Ave, R=Roosevelt
        </p>
        {calendarData.map((week, weekIndex) => (
          <div key={weekIndex} className="mb-4 sm:mb-6">
            <h3 className="text-sm sm:text-lg font-semibold text-gray-700 mb-2 sm:mb-3">Week {weekIndex + 1}</h3>
            <div className="grid grid-cols-7 gap-0.5 sm:gap-2">
              {week.map((day, dayIndex) => (
                <div 
                  key={dayIndex} 
                  className={`p-1 sm:p-3 rounded text-center transition-all ${
                    day.hours > 0 ? 'bg-blue-100 border border-blue-300' : 'bg-gray-100'
                  }`}
                  title={day.storeName ? `${day.storeName} - ${day.date}` : day.date}
                >
                  <div className="text-xs font-medium text-gray-500 mb-0.5 sm:mb-1">
                    {day.date ? day.date.split('-')[2] : ''}
                  </div>
                  <div className="font-semibold text-xs text-gray-700">{day.day}</div>
                  <div className="text-sm sm:text-lg font-bold text-gray-900 my-0.5 sm:my-1">{day.hours || '-'}</div>
                  {day.store && (
                    <div className="text-xs font-medium text-blue-700 bg-blue-200 rounded px-0.5 py-0.5">
                      {day.store}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="bg-gray-100 px-3 sm:px-6 py-2 sm:py-3 text-center text-xs text-gray-500">
        Generated on {new Date().toLocaleString()}
      </div>
    </div>
  );
};

export default PaystubViewer;